export const prefersReducedMotion = (): boolean =>
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

// jsdom has no Web Animations; without it the motion simply cuts.
export const canAnimate = <T extends Element>(el: T | null): el is T =>
  !!el && typeof el.animate === "function";

/// Motion tokens as WAAPI wants them; the CSS custom property is the source.
export const motionToken = (name: string, fallback: string): string =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim() ||
  fallback;

export const durationMs = (name: string, fallback: number): number => {
  const value = motionToken(name, `${fallback}ms`);
  const ms = parseFloat(value);
  return value.endsWith("ms") ? ms : value.endsWith("s") ? ms * 1000 : fallback;
};

const animatable = (el: HTMLElement | null): el is HTMLElement =>
  canAnimate(el) && !prefersReducedMotion();

const short = (ease: string, fallback: string): KeyframeAnimationOptions => ({
  duration: durationMs("--duration-short", 200),
  easing: motionToken(ease, fallback),
});

/// Commits `change`, which must render synchronously (inside `flushSync`), and
/// eases `el` from its height before to its height after, clipped meanwhile.
export const tweenHeight = (
  el: HTMLElement | null,
  change: () => void,
): void => {
  if (!animatable(el)) {
    change();
    return;
  }
  const from = el.getBoundingClientRect().height;
  change();
  const to = el.getBoundingClientRect().height;
  if (from === to) return;
  el.style.overflow = "hidden";
  const unclip = () => {
    el.style.overflow = "";
  };
  void el
    .animate(
      [{ height: `${from}px` }, { height: `${to}px` }],
      short("--ease-move", "ease-in-out"),
    )
    .finished.then(unclip, unclip);
};

/// Commits `change` (rendered synchronously) and glides `el` from where it sat in
/// `frame` to where it lands; measured in the frame, so a moving frame can't jolt it.
export const glide = (
  el: HTMLElement | null,
  frame: HTMLElement | null,
  change: () => void,
): void => {
  if (!animatable(el) || !frame) {
    change();
    return;
  }
  const offset = () =>
    el.getBoundingClientRect().top - frame.getBoundingClientRect().top;
  const from = offset();
  change();
  if (!el.isConnected) return;
  const by = from - offset();
  if (by === 0) return;
  el.animate(
    [{ transform: `translateY(${by}px)` }, { transform: "none" }],
    short("--ease-move", "ease-in-out"),
  );
};

/// Fades in `el`, which a change has just brought back.
export const fadeIn = (el: HTMLElement | null): void => {
  if (!animatable(el)) return;
  el.animate(
    [{ opacity: 0 }, { opacity: 1 }],
    short("--ease-enter", "ease-out"),
  );
};
